<template>
  <div class="campaign-contacts-page">
    <div class="page-back-button">
      <router-link
        :to="{ name: 'smart_ai_email_campaign' }"
        class="back-link"
      >
        <fluent-icon icon="chevron-left" size="16" />
        {{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.BACK') }}
      </router-link>
    </div>

    <div class="campaign-contacts-container">
      <div class="mb-4">
        <h2 v-if="campaign" class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          {{ campaign.title }}
        </h2>
        <h2 v-else-if="isLoading" class="text-xl font-semibold text-slate-400 dark:text-slate-500">
          Loading...
        </h2>
        <p v-if="campaign" class="text-sm text-slate-600 dark:text-slate-400">
          {{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.SUBTITLE', {
            total: campaign.total_contacts || 0,
            emails: campaign.emails_per_contact || 0
          }) }}
        </p>
      </div>

      <!-- Bulk Actions Bar -->
      <div v-if="campaign && selectedContacts.length > 0" class="bulk-actions-bar">
        <div class="bulk-actions-content">
          <span class="bulk-actions-text">
            {{ selectedContacts.length }} {{ selectedContacts.length === 1 ? 'contact' : 'contacts' }} selected
          </span>
          <div class="bulk-actions-buttons">
            <woot-button
              v-if="selectableContactsOnPage.length > 0 && !isAllOnPageSelected"
              variant="clear"
              size="small"
              @click="selectThisPage"
            >
              Select this page ({{ selectableContactsOnPage.length }})
            </woot-button>
            <woot-button
              v-if="!isAllSelected"
              variant="clear"
              size="small"
              :is-loading="isFetchingAllIds"
              :disabled="isFetchingAllIds"
              @click="toggleSelectAll"
            >
              Select all ({{ totalContactsCount }})
            </woot-button>
            <woot-button
              variant="smooth"
              size="small"
              icon="checkmark"
              color-scheme="success"
              @click="bulkApproveSelectedContacts"
            >
              Bulk Approve
            </woot-button>
            <woot-button
              variant="clear"
              size="small"
              icon="arrow-clockwise"
              color-scheme="secondary"
              @click="bulkRegenerateSelectedContacts"
            >
              Bulk Regenerate
            </woot-button>
            <woot-button
              variant="clear"
              size="small"
              icon="dismiss-circle"
              color-scheme="alert"
              @click="bulkCancelSelectedContacts"
            >
              Cancel
            </woot-button>
            <woot-button
              v-if="selectedContacts.length > 0"
              variant="clear"
              size="small"
              @click="clearSelection"
            >
              Clear Selection
            </woot-button>
          </div>
        </div>
      </div>

      <!-- Contacts Table: sticky header, scrollable body; loader only inside table area -->
      <div
        class="campaign-contacts-table-wrapper"
        :class="{ 'bulk-bar-open': selectedContacts.length > 0 }"
      >
        <div v-if="isLoading" class="table-loading-inner">
          <spinner size="large" />
        </div>
        <template v-else-if="campaign">
        <div class="table-scroll-wrapper">
          <table class="campaign-contacts-table">
            <thead>
              <tr>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.CONTACT') }}</th>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.STATUS') }}</th>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.EMAIL') }}</th>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.DRAFT_STATUS') }}</th>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.SENT') }}</th>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.TO_BE_SENT') }}</th>
                <th>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.ACTIONS') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="contact in paginatedContacts">
                <tr
                  :key="contact.id"
                  :class="{ 'row-selected': isContactSelected(contact.id) }"
                  @mouseenter="onRowHover(contact.id)"
                  @mouseleave="onRowLeave"
                >
                  <!-- Contact column with selector (same as contact table: checkbox on hover/selected, avatar otherwise) -->
                  <td class="table-cell-contact">
                    <div class="name-cell-wrapper">
                      <!-- Checkbox shown on hover or when selected (selectable contacts only) -->
                      <label
                        v-if="canSelectContact(contact) && (isRowHovered(contact.id) || isContactSelected(contact.id))"
                        class="checkbox-wrapper"
                        @click.stop
                      >
                        <input
                          type="checkbox"
                          class="checkbox"
                          :checked="isContactSelected(contact.id)"
                          @change="toggleContactSelection(contact.id)"
                        />
                      </label>
                      <!-- Avatar initials when not showing checkbox -->
                      <span
                        v-else
                        class="contact-avatar-initials"
                        :title="contact.name || contact.email"
                      >
                        {{ getContactInitials(contact) }}
                      </span>
                      <button
                        class="contact-name-btn"
                        @click="openContactPanel(contact)"
                      >
                        <span class="contact-name">{{ contact.name || 'Unknown' }}</span>
                        <span class="contact-email">{{ contact.email }}</span>
                      </button>
                    </div>
                  </td>

                  <!-- Status -->
                  <td class="table-cell-status">
                    <span :class="['status-pill', getStatusBadgeClass(contact.status)]">
                      {{ (contact.status || '').toLowerCase() }}
                    </span>
                  </td>

                  <!-- Email (View Button) -->
                  <td class="table-cell-email">
                    <button
                      type="button"
                      class="view-emails-btn"
                      @click="toggleEmailList(contact)"
                    >
                      <fluent-icon icon="mail" size="16" />
                      <span>{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.TABLE.VIEW_EMAILS') }}</span>
                      <fluent-icon
                        :icon="isEmailListOpen(contact.id) ? 'chevron-up' : 'chevron-down'"
                        size="14"
                        class="view-emails-chevron"
                      />
                    </button>
                  </td>

                  <!-- Draft Status (Combined) -->
                  <td class="table-cell-draft-status">
                    <span class="draft-status-text">
                      <template v-if="getContactDraftStatusParts(contact).length === 0">All Pending</template>
                      <template v-else>
                        <span
                          v-for="(part, idx) in getContactDraftStatusParts(contact)"
                          :key="idx"
                        >
                          <span :class="{ 'draft-status-executed': part.isExecuted }">{{ part.text }}</span><span v-if="idx < getContactDraftStatusParts(contact).length - 1">, </span>
                        </span>
                      </template>
                    </span>
                    <div class="draft-status-dots">
                      <span
                        v-for="step in contact.steps"
                        :key="step.step"
                        :class="['status-dot', getStepStatusDot(step.status)]"
                        v-tooltip.top="step.status"
                      />
                    </div>
                  </td>

                  <!-- Sent -->
                  <td class="table-cell-num">
                    {{ getContactSentCount(contact) }}
                  </td>

                  <!-- To Be Sent -->
                  <td class="table-cell-num">
                    {{ getContactToBeSentCount(contact) }}
                  </td>

                  <!-- Actions (hidden when all drafts are executed) -->
                  <td class="table-cell-actions">
                    <div v-if="!hasAllDraftsExecuted(contact)" class="actions-icons">
                      <woot-button
                        v-tooltip.top="$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.ACTIONS.REGENERATE_ALL')"
                        variant="clear"
                        size="small"
                        icon="arrow-clockwise"
                        color-scheme="secondary"
                        class="action-btn action-btn-regenerate"
                        @click="regenerateAllDrafts(contact)"
                      />
                      <woot-button
                        v-if="canApproveAnyDraft(contact)"
                        v-tooltip.top="$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.ACTIONS.APPROVE_ALL')"
                        variant="clear"
                        size="small"
                        icon="checkmark"
                        color-scheme="success"
                        class="action-btn action-btn-approve"
                        @click="approveAllDrafts(contact)"
                      />
                      <woot-button
                        v-tooltip.top="$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.ACTIONS.CANCEL_ALL')"
                        variant="clear"
                        size="small"
                        icon="dismiss-circle"
                        color-scheme="alert"
                        class="action-btn action-btn-cancel"
                        @click="cancelAllDrafts(contact)"
                      />
                    </div>
                  </td>
                </tr>

                <!-- Email List Dropdown Row -->
                <tr
                  v-if="isEmailListOpen(contact.id)"
                  :key="`${contact.id}-emails`"
                  class="email-list-row"
                >
                  <td colspan="7" class="email-list-cell">
                    <div class="email-list-container">
                      <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                        {{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.EMAIL_LIST.TITLE') }}
                      </h4>
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div
                          v-for="step in contact.steps"
                          :key="step.step"
                          class="email-card cursor-pointer p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-woot-500 dark:hover:border-woot-500 transition-all duration-200 hover:shadow-md"
                          @click="openDraftPreview(contact, step)"
                        >
                          <div class="flex items-start justify-between mb-2">
                            <div class="flex items-center gap-2">
                              <span :class="getStepStatusDot(step.status)" class="w-3 h-3 rounded-full" />
                              <span class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {{ step.title }}
                              </span>
                            </div>
                            <fluent-icon icon="chevron-right" size="16" class="text-slate-400" />
                          </div>
                          <div class="space-y-1">
                            <p class="text-xs text-slate-600 dark:text-slate-400">
                              <span class="font-medium">{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.EMAIL_LIST.STATUS') }}:</span> 
                              {{ step.status }}
                            </p>
                            <p class="text-xs text-slate-600 dark:text-slate-400">
                              <span class="font-medium">{{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.EMAIL_LIST.SCHEDULED') }}:</span> 
                              {{ step.scheduled_at_formatted }}
                            </p>
                            <div v-if="step.draft_id" class="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                              <div class="flex gap-1 flex-wrap">
                                <woot-button
                                  v-if="canRegenerateDraft(step)"
                                  variant="clear"
                                  size="tiny"
                                  icon="arrow-clockwise"
                                  color-scheme="secondary"
                                  @click.stop="regenerateSingleDraft(contact, step)"
                                >
                                  {{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.EMAIL_LIST.REGENERATE') }}
                                </woot-button>
                                <woot-button
                                  v-if="canApproveDraft(step)"
                                  variant="clear"
                                  size="tiny"
                                  icon="checkmark"
                                  color-scheme="success"
                                  @click.stop="approveSingleDraft(contact, step)"
                                >
                                  {{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.EMAIL_LIST.APPROVE') }}
                                </woot-button>
                                <woot-button
                                  v-if="canCancelDraft(step)"
                                  variant="clear"
                                  size="tiny"
                                  icon="dismiss-circle"
                                  color-scheme="alert"
                                  :loading="cancellingDraftId === step.draft_id"
                                  @click.stop="cancelDraftAndSuccessive(contact, step)"
                                >
                                  Cancel
                                </woot-button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <div v-if="contacts.length === 0" class="text-center py-8 text-slate-500">
            {{ $t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.EMPTY') }}
          </div>
        </div>
        </template>
      </div>
    </div>

    <!-- Pagination: lives outside the table wrapper so the bulk-actions-bar
         never obscures it regardless of scroll position -->
    <div v-if="totalContactsCount > 0" class="campaign-contacts-footer">
      <table-footer
        :current-page="currentPage"
        :total-count="totalContactsCount"
        :page-size="perPage"
        @page-change="onPageChange"
      />
    </div>

    <!-- Bulk Approve Confirmation Modal -->
    <woot-modal
      :show.sync="showBulkApproveConfirmation"
      :on-close="closeBulkApproveConfirmation"
    >
      <woot-modal-header
        :header-title="'Bulk Approve Confirmation'"
        :header-content="'Are you sure you want to bulk approve drafts for ' + bulkApproveData.contactCount + ' contact(s)?'"
      />
      <div class="modal-content">
        <div class="info-box">
          <p>
            <strong>Total drafts to approve:</strong> {{ bulkApproveData.totalDrafts }}
          </p>
        </div>
        <div class="note-box">
          <p>
            <strong>Note:</strong> Drafts will be approved sequentially for each contact. 
            Only drafts with "draft" status will be approved. 
            Already approved/executed drafts will be skipped. 
            Other statuses will be skipped without breaking execution.
          </p>
        </div>
      </div>
      <div class="modal-footer">
        <woot-button
          variant="clear"
          @click="closeBulkApproveConfirmation"
        >
          Cancel
        </woot-button>
        <woot-button
          color-scheme="success"
          :is-loading="isBulkApproving"
          @click="confirmBulkApprove"
        >
          Approve
        </woot-button>
      </div>
    </woot-modal>

    <!-- Bulk Approve Results Modal -->
    <woot-modal
      :show.sync="showBulkApproveResults"
      :on-close="closeBulkApproveResults"
    >
      <woot-modal-header
        :header-title="'Bulk Approval Complete'"
      />
      <div class="modal-content results-content">
        <div class="results-summary">
          <div class="summary-item">
            <span class="summary-label">Contacts Processed:</span>
            <span class="summary-value">{{ bulkApproveResults.total_contacts }}</span>
          </div>
          <div class="summary-item success">
            <span class="summary-label">✓ Successful:</span>
            <span class="summary-value">{{ bulkApproveResults.successful_contacts }}</span>
          </div>
          <div class="summary-item error">
            <span class="summary-label">✗ Failed:</span>
            <span class="summary-value">{{ bulkApproveResults.failed_contacts }}</span>
          </div>
        </div>
        <div class="results-details">
          <div class="detail-item">
            <span class="detail-label">Drafts Approved:</span>
            <span class="detail-value">{{ bulkApproveResults.total_approved }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Drafts Skipped:</span>
            <span class="detail-value">{{ bulkApproveResults.total_skipped }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Drafts Failed:</span>
            <span class="detail-value">{{ bulkApproveResults.total_failed }}</span>
          </div>
        </div>
        <div v-if="hasErrors" class="results-errors">
          <h4 class="errors-title">Errors:</h4>
          <div
            v-for="contactResult in contactsWithErrors"
            :key="contactResult.contact_id"
            class="error-item"
          >
            <p class="error-contact">{{ contactResult.contact_name }}:</p>
            <ul class="error-list">
              <li v-for="(error, index) in contactResult.errors" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <woot-button
          @click="closeBulkApproveResults"
        >
          Close
        </woot-button>
      </div>
    </woot-modal>

    <!-- Reusable Action Confirmation Modal (Regenerate all / Approve all / Cancel all) -->
    <woot-modal
      :show.sync="showActionConfirmation"
      :on-close="closeActionConfirmation"
    >
      <woot-modal-header
        :header-title="actionConfirmation.title"
        :header-content="actionConfirmation.message"
      />
      <div class="modal-footer">
        <woot-button
          variant="clear"
          @click="closeActionConfirmation"
        >
          {{ actionConfirmation.cancelLabel || 'Cancel' }}
        </woot-button>
        <woot-button
          :color-scheme="actionConfirmation.confirmColorScheme || 'primary'"
          :is-loading="isActionConfirmationProcessing"
          @click="confirmActionConfirmation"
        >
          {{ actionConfirmation.confirmLabel }}
        </woot-button>
      </div>
    </woot-modal>

    <!-- Contact Info Panel Modal -->
    <woot-modal
      :show.sync="showContactPanel"
      :on-close="closeContactPanel"
      class="contact-info-modal"
    >
      <div class="modal-content-wrapper">
        <contact-info-panel
          v-if="selectedContactData"
          :contact="selectedContactData"
          :on-close="closeContactPanel"
          :show-avatar="true"
          :show-close-button="true"
          :is-modal-view="true"
        />
      </div>
    </woot-modal>

    <!-- Draft Preview Modal -->
    <woot-modal
      :show.sync="showDraftModal"
      :on-close="closeDraftModal"
      :show-close-button="false"
      class="draft-modal"
    >
      <div class="draft-modal-content">
        <div v-if="selectedDraft" class="draft-viewer">
          <!-- Header: title left, action buttons right -->
          <div class="draft-header">
            <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100 draft-modal-title">
              {{ $t('CAMPAIGN.SMART_AI_EMAIL.DRAFT.TITLE', { step: selectedStep?.step, name: selectedContact?.name }) }}
            </h3>
            <div class="draft-header-actions">
              <woot-button
                v-tooltip.top="$t('SCHEDULED_DRAFTS.VIEWER.FULLSCREEN') || 'Fullscreen'"
                variant="clear"
                size="medium"
                icon="fullscreen"
                color-scheme="secondary"
                view-box="-4 -4 24 24"
                @click="openDraftFullscreen"
              />
              <woot-button
                v-tooltip.top="'Close'"
                variant="clear"
                size="medium"
                icon="dismiss"
                color-scheme="secondary"
                @click="closeDraftModal"
              />
            </div>
          </div>

          <!-- Status & Info (status, trigger label, macro, scheduled time) -->
          <div class="draft-info-grid">
            <div class="info-item">
              <span class="info-label">Status</span>
              <span :class="getStatusBadgeClass(selectedDraft.status)">
                {{ selectedDraft.status_display || selectedDraft.status }}
              </span>
            </div>
            <div v-if="selectedDraft.label" class="info-item info-item-trigger-label">
              <span class="info-label">Trigger Label</span>
              <span
                v-if="selectedDraft.label.title"
                class="info-value label-badge-inline"
                :style="{ color: selectedDraft.label.color || 'inherit' }"
              >
                {{ selectedDraft.label.title }}
              </span>
            </div>
            <div v-if="selectedDraft.macro" class="info-item">
              <span class="info-label">Macro</span>
              <span class="info-value">{{ selectedDraft.macro.name }}</span>
            </div>
            <div class="info-item info-item-scheduled-picker">
              <span class="info-label">Scheduled At</span>
              <span v-if="!isEditing">{{ formatDateTime(selectedDraft.scheduled_at) }}</span>
              <woot-date-time-picker
                v-else
                :value="editForm.scheduled_at"
                :append-to-body="true"
                :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
                :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
                class="draft-modal-datetime-picker"
                @change="onDraftScheduledAtChange"
              />
            </div>
          </div>

          <!-- Subject -->
          <div v-if="selectedDraft.email_subject || isEditing" class="draft-section">
            <label class="section-label">Subject</label>
            <input
              v-if="isEditing"
              v-model="editForm.email_subject"
              type="text"
              class="subject-input"
              placeholder="Email Subject"
            />
            <p v-else class="section-content">{{ selectedDraft.email_subject }}</p>
          </div>

          <!-- Message Content -->
          <div class="draft-section">
            <label class="section-label">Message Content</label>
            <div v-if="isEditing" class="editor-wrapper">
              <woot-message-editor
                v-model="editForm.message_content"
                class="message-editor"
                :placeholder="'Email content...'"
                :min-height="200"
                :is-format-mode="true"
                @blur="onEditorBlur"
              />
            </div>
            <div v-else class="content-display" v-html="formattedMessageContent" />
          </div>

          <!-- Attachments Section -->
          <div v-if="isEditing || (selectedDraft.attachments && selectedDraft.attachments.length > 0)" class="draft-section">
            <label class="section-label">Attachments</label>
            
            <!-- Existing Attachments -->
            <div v-if="selectedDraft.attachments && selectedDraft.attachments.length > 0" class="attachments-list">
              <div
                v-for="(attachment, index) in selectedDraft.attachments"
                :key="attachment.id"
                class="attachment-item"
              >
                <fluent-icon icon="document" size="16" />
                <span class="attachment-name">{{ attachment.filename }}</span>
                <span v-if="attachment.byte_size" class="text-xs text-slate-500 ml-2">
                  ({{ formatFileSize(attachment.byte_size) }})
                </span>
                <woot-button
                  v-if="isEditing"
                  variant="clear"
                  size="tiny"
                  icon="dismiss"
                  color-scheme="alert"
                  @click="removeExistingAttachment(attachment.id, index)"
                />
              </div>
            </div>

            <!-- New Attachments (being uploaded) -->
            <div v-if="isEditing && newAttachments.length > 0" class="attachments-list">
              <div
                v-for="(file, index) in newAttachments"
                :key="'new-' + index"
                class="attachment-item"
              >
                <fluent-icon icon="document" size="16" />
                <span class="attachment-name">{{ file.name }}</span>
                <span v-if="file.size" class="text-xs text-slate-500 ml-2">
                  ({{ formatFileSize(file.size) }})
                </span>
                <woot-button
                  variant="clear"
                  size="tiny"
                  icon="dismiss"
                  color-scheme="alert"
                  @click="removeNewAttachment(index)"
                />
              </div>
            </div>

            <!-- Add Attachments (Edit Mode) -->
            <div v-if="isEditing" class="attachment-upload">
              <input
                ref="fileInput"
                type="file"
                multiple
                class="hidden"
                @change="onFileSelect"
              />
              <woot-button
                variant="smooth"
                color-scheme="secondary"
                icon="attach"
                size="small"
                @click="$refs.fileInput.click()"
              >
                Add Attachment
              </woot-button>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="draft-actions">
            <template v-if="!isEditing">
              <woot-button
                v-if="selectedDraft.can_edit"
                variant="smooth"
                color-scheme="secondary"
                icon="edit"
                @click="startEdit"
              >
                Edit
              </woot-button>
              <woot-button
                v-if="selectedDraft.can_edit && canRegenerateDraftByStatus(selectedDraft.status)"
                variant="smooth"
                color-scheme="secondary"
                icon="arrow-clockwise"
                :loading="isRegenerating"
                @click="showRegeneratePrompt = true"
              >
                Regenerate
              </woot-button>
              <woot-button
                v-if="selectedDraft.can_approve"
                variant="smooth"
                color-scheme="success"
                icon="checkmark"
                :loading="isApproving"
                @click="approveDraft"
              >
                Approve
              </woot-button>
              <woot-button
                v-if="canCancelDraftByStatus(selectedDraft.status)"
                variant="smooth"
                color-scheme="alert"
                icon="dismiss-circle"
                :loading="isCancellingDraft"
                @click="cancelDraftFromPreview"
              >
                Cancel draft
              </woot-button>
            </template>
            <template v-else>
              <woot-button
                variant="smooth"
                color-scheme="secondary"
                @click="cancelEdit"
              >
                Cancel
              </woot-button>
              <woot-button
                variant="smooth"
                color-scheme="primary"
                :loading="isSaving"
                @click="saveEdit"
              >
                Save Changes
              </woot-button>
            </template>
          </div>
        </div>

        <!-- No Draft Found -->
        <div v-else class="no-draft-message">
          <fluent-icon icon="document" size="48" class="text-slate-400" />
          <h3 class="text-lg font-medium text-slate-700 dark:text-slate-300 mt-4">
            Draft Not Generated Yet
          </h3>
          <p class="text-sm text-slate-500 mt-2">
            The email draft for this step hasn't been generated yet. Please wait for the automation to create it.
          </p>
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            class="mt-4"
            @click="closeDraftModal"
          >
            Close
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <!-- Regenerate Prompt Modal -->
    <woot-modal
      :show.sync="showRegeneratePrompt"
      :on-close="() => showRegeneratePrompt = false"
      class="regenerate-modal"
    >
      <div class="regenerate-content p-6">
        <h3 class="text-lg font-semibold mb-4">Regenerate Draft</h3>
        <p class="text-sm text-slate-600 mb-4">
          Optionally provide instructions for regenerating the email:
        </p>
        <textarea
          v-model="regeneratePrompt"
          class="w-full border rounded-md p-3 mb-4"
          rows="4"
          placeholder="E.g., Make it more formal, add a call to action..."
        />
        <div class="flex justify-end gap-2">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            @click="showRegeneratePrompt = false"
          >
            Cancel
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            :loading="isRegenerating"
            @click="regenerateDraft"
          >
            Regenerate
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner.vue';
import ContactInfoPanel from 'dashboard/routes/dashboard/contacts/components/ContactInfoPanel.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import CampaignsAPI from 'dashboard/api/campaigns';
import ContactAPI from 'dashboard/api/contacts';
import ScheduledDraftsAPI from 'dashboard/api/scheduledDrafts';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';

export default {
  components: {
    Spinner,
    ContactInfoPanel,
    WootMessageEditor,
    TableFooter,
    WootDateTimePicker,
  },
  mixins: [alertMixin],
  data() {
    return {
      isLoading: true,
      campaign: null,
      contacts: [],
      showContactPanel: false,
      selectedContactData: null,
      // Draft modal state
      showDraftModal: false,
      selectedDraft: null,
      selectedStep: null,
      selectedContact: null,
      isEditing: false,
      isSaving: false,
      isApproving: false,
      isRegenerating: false,
      showRegeneratePrompt: false,
      regeneratePrompt: '',
      newAttachments: [],
      removedAttachmentIds: [],
      editForm: {
        message_content: '',
        email_subject: '',
        scheduled_at: null,
      },
      // Email list expansion state
      openEmailLists: [],
      // Bulk selection state
      selectedContacts: [],
      hoveredContactId: null,
      isFetchingAllIds: false,
      isBulkApproving: false,
      showBulkApproveConfirmation: false,
      showBulkApproveResults: false,
      bulkApproveData: {
        contactCount: 0,
        totalDrafts: 0,
      },
      bulkApproveResults: {
        total_contacts: 0,
        successful_contacts: 0,
        failed_contacts: 0,
        total_approved: 0,
        total_skipped: 0,
        total_failed: 0,
        contact_results: [],
      },
      // Pagination (server-side; total from API)
      currentPage: 1,
      perPage: 20,
      totalContactsCount: 0,
      // Cancel draft (single + successive)
      cancellingDraftId: null,
      isCancellingDraft: false,
      // Reusable action confirmation (regenerate all / approve all / cancel all)
      showActionConfirmation: false,
      actionConfirmation: {
        type: null,
        contact: null,
        title: '',
        message: '',
        confirmLabel: '',
        cancelLabel: 'Cancel',
        confirmColorScheme: 'primary',
      },
      isActionConfirmationProcessing: false,
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    campaignId() {
      return this.$route.params.campaigns_id;
    },
    formattedMessageContent() {
      if (!this.selectedDraft?.message_content) return '';
      // Basic HTML formatting - you can use a markdown parser here
      return this.selectedDraft.message_content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
    },
    selectableContacts() {
      return this.contacts.filter(c => !this.hasAllDraftsExecuted(c));
    },
    totalPages() {
      if (this.totalContactsCount === 0) return 1;
      return Math.ceil(this.totalContactsCount / this.perPage) || 1;
    },
    paginatedContacts() {
      // Server-side pagination: API returns only current page
      return this.contacts;
    },
    paginationStart() {
      if (this.totalContactsCount === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalContactsCount);
    },
    visiblePageNumbers() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;
      for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
          range.push(i);
        }
      }
      for (const i of range) {
        if (l !== undefined && i - l !== 1) rangeWithDots.push(-1);
        rangeWithDots.push(i);
        l = i;
      }
      return rangeWithDots.filter(p => p > 0);
    },
    isAllSelected() {
      if (this.totalContactsCount === 0) return false;
      return this.selectedContacts.length >= this.totalContactsCount;
    },
    isSomeSelected() {
      return this.selectedContacts.length > 0 && !this.isAllSelected;
    },
    selectableContactsOnPage() {
      return this.paginatedContacts.filter(c => !this.hasAllDraftsExecuted(c));
    },
    isAllOnPageSelected() {
      const onPage = this.selectableContactsOnPage;
      if (onPage.length === 0) return false;
      return onPage.every(c => this.selectedContacts.includes(c.id));
    },
    hasErrors() {
      return this.bulkApproveResults.contact_results.some(r => r.errors && r.errors.length > 0);
    },
    contactsWithErrors() {
      return this.bulkApproveResults.contact_results.filter(r => r.errors && r.errors.length > 0);
    },
  },
  watch: {
    campaignId(newId, oldId) {
      if (newId && oldId !== undefined && newId !== oldId) {
        this.resetStateForNewCampaign();
        this.fetchCampaignContacts();
      }
    },
    perPage() {
      this.currentPage = 1;
      this.syncPageToRoute();
      this.fetchCampaignContacts();
    },
  },
  mounted() {
    const pageFromRoute = parseInt(this.$route.query.page, 10);
    if (Number.isFinite(pageFromRoute) && pageFromRoute >= 1) {
      this.currentPage = pageFromRoute;
    }
    this.fetchCampaignContacts();
  },
  methods: {
    resetStateForNewCampaign() {
      this.campaign = null;
      this.contacts = [];
      this.totalContactsCount = 0;
      this.currentPage = 1;
      this.selectedContacts = [];
      this.openEmailLists = [];
      this.showContactPanel = false;
      this.selectedContactData = null;
      this.showDraftModal = false;
      this.selectedDraft = null;
      this.selectedStep = null;
      this.selectedContact = null;
      this.bulkApproveResults = {
        total_contacts: 0,
        successful_contacts: 0,
        failed_contacts: 0,
        total_approved: 0,
        total_skipped: 0,
        total_failed: 0,
        contact_results: [],
      };
    },
    syncPageToRoute() {
      const query = { ...this.$route.query };
      if (this.currentPage <= 1) {
        delete query.page;
      } else {
        query.page = String(this.currentPage);
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
    async fetchCampaignContacts() {
      const requestedCampaignId = this.campaignId;
      if (!requestedCampaignId) return;
      this.isLoading = true;
      try {
        const response = await CampaignsAPI.getContacts(
          requestedCampaignId,
          this.currentPage,
          this.perPage
        );
        if (this.campaignId !== requestedCampaignId) return;
        this.campaign = response.data.campaign;
        this.contacts = response.data.contacts || [];
        this.totalContactsCount = response.data.meta?.total_count ?? 0;
        const totalPages = Math.ceil(this.totalContactsCount / this.perPage) || 1;
        if (this.currentPage > totalPages && totalPages > 0) {
          this.currentPage = totalPages;
        }
        this.syncPageToRoute();
      } catch (error) {
        if (this.campaignId !== requestedCampaignId) return;
        this.showAlert(this.$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.FETCH_ERROR'));
      } finally {
        if (this.campaignId === requestedCampaignId) {
          this.isLoading = false;
        }
      }
    },
    async openContactPanel(contact) {
      try {
        const response = await ContactAPI.getContact(contact.id);
        this.selectedContactData = response.data.payload;
        this.showContactPanel = true;
      } catch (error) {
        this.selectedContactData = {
          id: contact.id,
          name: contact.name,
          email: contact.email,
          phone_number: contact.phone_number,
          custom_attributes: {},
        };
        this.showContactPanel = true;
      }
    },
    closeContactPanel() {
      this.showContactPanel = false;
      this.selectedContactData = null;
    },
    async openDraftPreview(contact, step) {
      if (!step.draft_id || !step.conversation_id) {
        // No draft generated yet - show modal with message
        this.selectedContact = contact;
        this.selectedStep = step;
        this.selectedDraft = null;
        this.showDraftModal = true;
        return;
      }

      // Open draft details in modal instead of navigating away
      try {
        const draft = await this.$store.dispatch('scheduledDrafts/fetchDraft', {
          conversationId: step.conversation_id,
          draftId: step.draft_id,
        });
        this.$store.dispatch('scheduledDrafts/setSelectedDraft', step.draft_id);
        this.selectedDraft = draft;
        this.selectedContact = contact;
        this.selectedStep = step;
        this.showDraftModal = true;
      } catch (error) {
        console.error('Failed to open draft:', error);
        this.showAlert('Failed to open draft details');
      }
    },
    openDraftFullscreen() {
      if (!this.selectedStep || !this.selectedStep.conversation_id || !this.selectedStep.draft_id) return;
      const conversationId = this.selectedStep.conversation_id;
      const draftId = this.selectedStep.draft_id;
      this.closeDraftModal();
      this.$router.push({
        name: 'scheduled_drafts',
        params: {
          accountId: this.currentAccountId,
          conversation_id: conversationId,
        },
        query: {
          draft_id: String(draftId),
          from: 'campaign',
          campaign_id: this.campaignId,
        },
      });
    },
    closeDraftModal() {
      this.showDraftModal = false;
      this.selectedDraft = null;
      this.selectedStep = null;
      this.selectedContact = null;
      this.isEditing = false;
      this.newAttachments = [];
      this.removedAttachmentIds = [];
      this.editForm = {
        message_content: '',
        email_subject: '',
        scheduled_at: null,
      };
    },
    onDraftScheduledAtChange(value) {
      this.editForm.scheduled_at = value;
    },
    startEdit() {
      this.editForm = {
        message_content: this.selectedDraft.message_content || '',
        email_subject: this.selectedDraft.email_subject || '',
        scheduled_at: this.selectedDraft.scheduled_at
          ? new Date(this.selectedDraft.scheduled_at)
          : null,
      };
      this.newAttachments = [];
      this.removedAttachmentIds = [];
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.newAttachments = [];
      this.removedAttachmentIds = [];
      this.editForm = {
        message_content: '',
        email_subject: '',
        scheduled_at: null,
      };
    },
    onFileSelect(event) {
      const files = Array.from(event.target.files);
      this.newAttachments = [...this.newAttachments, ...files];
      console.log('Files selected:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));
      // Clear the input so the same file can be selected again
      event.target.value = '';
    },
    removeExistingAttachment(attachmentId, index) {
      // Mark for removal on server
      this.removedAttachmentIds.push(attachmentId);
      // Remove from display
      this.selectedDraft.attachments.splice(index, 1);
      console.log('Marked attachment for removal:', attachmentId);
    },
    removeNewAttachment(index) {
      // Remove from new attachments array
      this.newAttachments.splice(index, 1);
      console.log('Removed new attachment at index:', index);
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    onEditorBlur() {
      // Handle editor blur if needed
    },
    async saveEdit() {
      this.isSaving = true;
      try {
        const scheduledAtISO = this.editForm.scheduled_at
          ? this.editForm.scheduled_at.toISOString()
          : null;

        const updateData = {
          message_content: this.editForm.message_content,
          email_subject: this.editForm.email_subject,
          scheduled_at: scheduledAtISO,
          attachments: this.newAttachments,
          removed_attachment_ids: this.removedAttachmentIds,
        };

        console.log('Saving draft with data:', {
          ...updateData,
          attachments: updateData.attachments.map(f => ({ name: f.name, size: f.size, type: f.type })),
        });

        await ScheduledDraftsAPI.update(
          this.selectedStep.conversation_id,
          this.selectedDraft.id,
          updateData
        );

        console.log('Draft saved successfully, fetching updated data...');

        // Refresh draft data
        const response = await ScheduledDraftsAPI.get(
          this.selectedStep.conversation_id,
          this.selectedDraft.id
        );
        
        console.log('Updated draft data:', {
          id: response.data.id,
          attachments: response.data.attachments,
        });
        
        this.selectedDraft = response.data;
        this.isEditing = false;
        this.newAttachments = [];
        this.removedAttachmentIds = [];
        this.showAlert('Draft updated successfully');
        
        // Refresh contacts list
        this.fetchCampaignContacts();
      } catch (error) {
        this.showAlert('Failed to update draft');
      } finally {
        this.isSaving = false;
      }
    },
    isDraftScheduledTimeInPast(scheduledAt) {
      if (!scheduledAt) return false;
      return new Date(scheduledAt) <= new Date();
    },
    approvePastTimeAlertMessage() {
      return this.$t(
        'CAMPAIGN.SMART_AI_EMAIL.CONTACTS.DRAFT_PREVIEW.APPROVE_PAST_TIME'
      );
    },
    regenerateErrorMessage(error) {
      const status = error.response?.status;
      if (status === 402) {
        return (
          this.$t(
            'CAMPAIGN.SMART_AI_EMAIL.CONTACTS.DRAFT_PREVIEW.REGENERATE_PAYMENT_OR_LIMIT'
          ) ||
          error.response?.data?.error ||
          'Request blocked (402). Check account limits or subscription.'
        );
      }
      const server =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        '';
      if (server) {
        return this.$t(
          'CAMPAIGN.SMART_AI_EMAIL.CONTACTS.DRAFT_PREVIEW.REGENERATE_ERROR',
          { error: server }
        );
      }
      return this.$t(
        'CAMPAIGN.SMART_AI_EMAIL.CONTACTS.DRAFT_PREVIEW.REGENERATE_ERROR',
        { error: 'Unknown error' }
      );
    },
    async approveDraft() {
      if (this.isDraftScheduledTimeInPast(this.selectedDraft?.scheduled_at)) {
        this.showAlert(this.approvePastTimeAlertMessage());
        return;
      }
      this.isApproving = true;
      try {
        await ScheduledDraftsAPI.approve(
          this.selectedStep.conversation_id,
          this.selectedDraft.id
        );

        // Refresh draft data
        const response = await ScheduledDraftsAPI.get(
          this.selectedStep.conversation_id,
          this.selectedDraft.id
        );
        this.selectedDraft = response.data;
        this.showAlert('Draft approved successfully');
        
        // Refresh contacts list
        this.fetchCampaignContacts();
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to approve draft';
        this.showAlert(
          /scheduled time has passed|in the past|past time/i.test(String(msg))
            ? this.approvePastTimeAlertMessage()
            : msg
        );
      } finally {
        this.isApproving = false;
      }
    },
    async regenerateDraft() {
      this.isRegenerating = true;
      try {
        await ScheduledDraftsAPI.regenerate(
          this.selectedStep.conversation_id,
          this.selectedDraft.id,
          this.regeneratePrompt
        );

        this.showRegeneratePrompt = false;
        this.regeneratePrompt = '';
        this.showAlert('Draft regeneration queued. Please refresh in a moment.');
        this.closeDraftModal();
        
        // Refresh contacts list after a delay
        setTimeout(() => this.fetchCampaignContacts(), 3000);
      } catch (error) {
        this.showAlert(this.regenerateErrorMessage(error));
      } finally {
        this.isRegenerating = false;
      }
    },
    goBackToCampaign() {
      // Close the modal and navigate back to campaign list
      this.closeDraftModal();
      this.$router.push({ name: 'smart_ai_email_campaign' });
    },
    async removeContact(contact) {
      // TODO: Implement remove contact from campaign
      this.showAlert('Remove contact feature coming soon');
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDateForInput(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    getStatusBadgeClass(status) {
      const baseClass = 'px-2 py-1 text-xs font-medium rounded-full';
      const statusClasses = {
        pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        executed: 'badge-executed',
        failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      };
      return `${baseClass} ${statusClasses[status] || statusClasses.pending}`;
    },
    getStepStatusDot(status) {
      const key = (status || '').toLowerCase();
      const statusColors = {
        pending: 'bg-yellow-500',
        draft: 'bg-yellow-500',
        approved: 'bg-green-500',
        executed: 'status-dot-executed',
        cancelled: 'status-dot-cancelled',
        failed: 'bg-red-500',
      };
      return statusColors[key] || 'bg-gray-500';
    },
    // New methods for table redesign
    toggleEmailList(contact) {
      const index = this.openEmailLists.indexOf(contact.id);
      if (index > -1) {
        this.openEmailLists.splice(index, 1);
      } else {
        // Only one email list open at a time (accordion)
        this.openEmailLists = [contact.id];
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchCampaignContacts();
    },
    isEmailListOpen(contactId) {
      return this.openEmailLists.includes(contactId);
    },
    getContactDraftStatusSummary(contact) {
      if (!contact.steps || contact.steps.length === 0) return 'N/A';
      const parts = this.getContactDraftStatusParts(contact);
      return parts.length > 0 ? parts.map(p => p.text).join(', ') : 'All Pending';
    },
    getContactDraftStatusParts(contact) {
      if (!contact.steps || contact.steps.length === 0) return [];
      const statusCounts = contact.steps.reduce((acc, step) => {
        const status = step.status || 'pending';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      const parts = [];
      if (statusCounts.approved) parts.push({ text: `${statusCounts.approved} Approved`, isExecuted: false });
      if (statusCounts.pending || statusCounts.draft) {
        const pendingCount = (statusCounts.pending || 0) + (statusCounts.draft || 0);
        parts.push({ text: `${pendingCount} Pending`, isExecuted: false });
      }
      if (statusCounts.executed) parts.push({ text: `${statusCounts.executed} Executed`, isExecuted: true });
      if (statusCounts.failed) parts.push({ text: `${statusCounts.failed} Failed`, isExecuted: false });
      return parts;
    },
    getContactSentCount(contact) {
      if (!contact.steps) return '0';
      return contact.steps.filter(step => step.status === 'executed').length;
    },
    getContactToBeSentCount(contact) {
      if (!contact.steps) return '0';
      return contact.steps.filter(step => 
        step.status === 'approved' || step.status === 'pending' || step.status === 'draft'
      ).length;
    },
    canApproveDraft(step) {
      return step.draft_id && (step.status === 'pending' || step.status === 'draft');
    },
    canRegenerateDraft(step) {
      if (!step.draft_id || !step.status) return false;
      return String(step.status).toLowerCase() !== 'executed';
    },
    canRegenerateDraftByStatus(status) {
      if (!status) return false;
      return String(status).toLowerCase() !== 'executed';
    },
    canCancelDraft(step) {
      if (!step.draft_id || !step.status) return false;
      const s = String(step.status).toLowerCase();
      return s === 'draft' || s === 'approved';
    },
    canCancelDraftByStatus(status) {
      if (!status) return false;
      const s = String(status).toLowerCase();
      return s === 'draft' || s === 'approved';
    },
    hasAllDraftsExecuted(contact) {
      if (!contact.steps || contact.steps.length === 0) return true;
      return contact.steps.every(
        step => String(step.status || '').toLowerCase() === 'executed'
      );
    },
    canSelectContact(contact) {
      return !this.hasAllDraftsExecuted(contact);
    },
    canApproveAnyDraft(contact) {
      if (!contact.steps || contact.steps.length === 0) return false;
      return contact.steps.some(
        step => ['draft', 'pending'].includes(String(step.status || '').toLowerCase())
      );
    },
    getContactInitials(contact) {
      const name = (contact.name || contact.email || '?').trim();
      if (!name) return '?';
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
      }
      return name.slice(0, 2).toUpperCase();
    },
    onRowHover(contactId) {
      this.hoveredContactId = contactId;
    },
    onRowLeave() {
      this.hoveredContactId = null;
    },
    isRowHovered(contactId) {
      return this.hoveredContactId === contactId;
    },
    async regenerateSingleDraft(contact, step) {
      if (!step.draft_id || !step.conversation_id) {
        this.showAlert('Draft not found');
        return;
      }

      try {
        await ScheduledDraftsAPI.regenerate(step.conversation_id, step.draft_id, '');
        this.showAlert('Draft regeneration started');
        // Refresh the contact list after a delay
        setTimeout(() => {
          this.fetchCampaignContacts();
        }, 2000);
      } catch (error) {
        console.error('Failed to regenerate draft:', error);
        this.showAlert(this.regenerateErrorMessage(error));
      }
    },
    async approveSingleDraft(contact, step) {
      if (!step.draft_id || !step.conversation_id) {
        this.showAlert('Draft not found');
        return;
      }

      if (this.isDraftScheduledTimeInPast(step.scheduled_at)) {
        this.showAlert(this.approvePastTimeAlertMessage());
        return;
      }

      try {
        await ScheduledDraftsAPI.approve(step.conversation_id, step.draft_id);
        this.showAlert('Draft approved successfully');
        this.fetchCampaignContacts();
      } catch (error) {
        console.error('Failed to approve draft:', error);
        const msg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to approve draft';
        this.showAlert(
          /scheduled time has passed|past/i.test(msg) ? this.approvePastTimeAlertMessage() : msg
        );
      }
    },
    async cancelDraftAndSuccessive(contact, step) {
      if (!step.draft_id || !step.conversation_id) {
        this.showAlert('Draft not found');
        return;
      }
      const currentStepNum = step.step ?? step.step_number ?? 0;
      const steps = (contact.steps || []).slice().sort((a, b) => (a.step ?? a.step_number ?? 0) - (b.step ?? b.step_number ?? 0));
      const toCancel = steps.filter(s => {
        const num = s.step ?? s.step_number ?? 0;
        if (num < currentStepNum) return false;
        const st = String(s.status || '').toLowerCase();
        return (st === 'draft' || st === 'approved') && s.draft_id && s.conversation_id;
      }).sort((a, b) => (a.step ?? a.step_number ?? 0) - (b.step ?? b.step_number ?? 0));
      this.cancellingDraftId = step.draft_id;
      try {
        for (const s of toCancel) {
          await ScheduledDraftsAPI.delete(s.conversation_id, s.draft_id);
        }
        this.showAlert(toCancel.length === 1 ? 'Draft cancelled' : `${toCancel.length} draft(s) cancelled (this and successive approved).`);
        this.fetchCampaignContacts();
      } catch (error) {
        console.error('Failed to cancel draft(s):', error);
        this.showAlert(error.response?.data?.error || 'Failed to cancel draft(s)');
      } finally {
        this.cancellingDraftId = null;
      }
    },
    async cancelDraftFromPreview() {
      if (!this.selectedContact || !this.selectedStep) return;
      this.isCancellingDraft = true;
      try {
        await this.cancelDraftAndSuccessive(this.selectedContact, this.selectedStep);
        this.closeDraftModal();
      } finally {
        this.isCancellingDraft = false;
      }
    },
    openRegenerateAllConfirmation(contact) {
      const draftsToRegenerate = contact.steps.filter(step =>
        step.draft_id && step.conversation_id && String(step.status || '').toLowerCase() !== 'executed'
      );
      if (draftsToRegenerate.length === 0) {
        this.showAlert('No drafts available to regenerate (executed drafts are skipped)');
        return;
      }
      this.actionConfirmation = {
        type: 'regenerate_all',
        contact,
        title: 'Regenerate all drafts',
        message: this.$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.CONFIRM.REGENERATE_ALL', {
          name: contact.name,
          count: draftsToRegenerate.length,
        }),
        confirmLabel: 'Regenerate',
        cancelLabel: 'Cancel',
        confirmColorScheme: 'secondary',
      };
      this.showActionConfirmation = true;
    },
    openApproveAllConfirmation(contact) {
      const now = new Date();
      const draftsToApprove = contact.steps.filter(step => {
        if (!step.draft_id || !step.conversation_id || !this.canApproveDraft(step)) {
          return false;
        }
        return step.scheduled_at && new Date(step.scheduled_at) > now;
      });
      if (draftsToApprove.length === 0) {
        const anyPast = contact.steps.some(
          s =>
            this.canApproveDraft(s) &&
            s.scheduled_at &&
            new Date(s.scheduled_at) <= now
        );
        this.showAlert(
          anyPast ? this.approvePastTimeAlertMessage() : 'No drafts available to approve'
        );
        return;
      }
      this.actionConfirmation = {
        type: 'approve_all',
        contact,
        title: 'Approve all drafts',
        message: this.$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.CONFIRM.APPROVE_ALL', {
          name: contact.name,
          count: draftsToApprove.length,
        }),
        confirmLabel: 'Approve',
        cancelLabel: 'Cancel',
        confirmColorScheme: 'success',
      };
      this.showActionConfirmation = true;
    },
    openCancelAllConfirmation(contact) {
      const draftsToCancel = contact.steps.filter(step =>
        step.draft_id && step.conversation_id && step.status !== 'executed'
      );
      if (draftsToCancel.length === 0) {
        this.showAlert('No drafts available to cancel');
        return;
      }
      this.actionConfirmation = {
        type: 'cancel_all',
        contact,
        title: 'Cancel all drafts',
        message: this.$t('CAMPAIGN.SMART_AI_EMAIL.CONTACTS.CONFIRM.CANCEL_ALL', {
          name: contact.name,
          count: draftsToCancel.length,
        }),
        confirmLabel: 'Cancel drafts',
        cancelLabel: 'Keep',
        confirmColorScheme: 'alert',
      };
      this.showActionConfirmation = true;
    },
    closeActionConfirmation() {
      if (this.isActionConfirmationProcessing) return;
      this.showActionConfirmation = false;
      this.actionConfirmation = { type: null, contact: null, title: '', message: '', confirmLabel: '', cancelLabel: 'Cancel', confirmColorScheme: 'primary' };
    },
    async confirmActionConfirmation() {
      const { type, contact } = this.actionConfirmation;
      if (!type || !contact) return;
      this.isActionConfirmationProcessing = true;
      try {
        if (type === 'regenerate_all') {
          await this.executeRegenerateAllDrafts(contact);
        } else if (type === 'approve_all') {
          await this.executeApproveAllDrafts(contact);
        } else if (type === 'cancel_all') {
          await this.executeCancelAllDrafts(contact);
        }
        this.isActionConfirmationProcessing = false;
        this.closeActionConfirmation();
      } finally {
        this.isActionConfirmationProcessing = false;
      }
    },
    async executeRegenerateAllDrafts(contact) {
      const draftsToRegenerate = contact.steps.filter(step =>
        step.draft_id && step.conversation_id && String(step.status || '').toLowerCase() !== 'executed'
      );
      if (draftsToRegenerate.length === 0) return;
      try {
        const promises = draftsToRegenerate.map(step =>
          ScheduledDraftsAPI.regenerate(step.conversation_id, step.draft_id, '')
        );
        await Promise.all(promises);
        this.showAlert(`Regenerating ${draftsToRegenerate.length} drafts`);
        setTimeout(() => this.fetchCampaignContacts(), 2000);
      } catch (error) {
        console.error('Failed to regenerate drafts:', error);
        this.showAlert(this.regenerateErrorMessage(error));
      }
    },
    async executeApproveAllDrafts(contact) {
      const now = new Date();
      const draftsToApprove = contact.steps.filter(step => {
        if (!step.draft_id || !step.conversation_id || !this.canApproveDraft(step)) {
          return false;
        }
        if (!step.scheduled_at || new Date(step.scheduled_at) <= now) {
          return false;
        }
        return true;
      });
      if (draftsToApprove.length === 0) {
        const anyPending = contact.steps.some(
          s =>
            this.canApproveDraft(s) &&
            s.scheduled_at &&
            new Date(s.scheduled_at) <= now
        );
        this.showAlert(
          anyPending
            ? this.approvePastTimeAlertMessage()
            : 'No drafts available to approve'
        );
        return;
      }
      try {
        const response = await CampaignsAPI.bulkApprove(this.campaignId, [contact.id]);
        if (response.status === 202) {
          this.showAlert('Bulk approve started in the background. The list will refresh shortly.');
          setTimeout(() => this.fetchCampaignContacts(), 3000);
          return;
        }
        const result = response.data;
        if (result.total_approved > 0) {
          let message = `Approved ${result.total_approved} draft(s) successfully`;
          if (result.total_skipped > 0) message += `\nSkipped ${result.total_skipped} draft(s)`;
          this.showAlert(message);
        } else {
          let message = 'No drafts were approved.\n\n';
          if (result.total_skipped > 0) message += `${result.total_skipped} draft(s) were skipped.\n\n`;
          if (result.contact_results?.[0]?.errors?.length > 0) {
            message += 'Reasons:\n';
            result.contact_results[0].errors.forEach(error => { message += `  • ${error}\n`; });
          }
          this.showAlert(message);
        }
        this.fetchCampaignContacts();
      } catch (error) {
        console.error('Failed to approve drafts:', error);
        this.showAlert('Failed to approve drafts');
      }
    },
    async executeCancelAllDrafts(contact) {
      const draftsToCancel = contact.steps.filter(step =>
        step.draft_id && step.conversation_id && step.status !== 'executed'
      );
      if (draftsToCancel.length === 0) return;
      try {
        const promises = draftsToCancel.map(step =>
          ScheduledDraftsAPI.delete(step.conversation_id, step.draft_id)
        );
        await Promise.all(promises);
        this.showAlert(`Cancelled ${draftsToCancel.length} drafts successfully`);
        this.fetchCampaignContacts();
      } catch (error) {
        console.error('Failed to cancel drafts:', error);
        this.showAlert('Failed to cancel some drafts');
      }
    },
    async regenerateAllDrafts(contact) {
      this.openRegenerateAllConfirmation(contact);
    },
    async approveAllDrafts(contact) {
      this.openApproveAllConfirmation(contact);
    },
    async cancelAllDrafts(contact) {
      this.openCancelAllConfirmation(contact);
    },
    // Bulk selection methods
    isContactSelected(contactId) {
      return this.selectedContacts.includes(contactId);
    },
    toggleContactSelection(contactId) {
      const index = this.selectedContacts.indexOf(contactId);
      if (index > -1) {
        this.selectedContacts.splice(index, 1);
      } else {
        this.selectedContacts.push(contactId);
      }
    },
    async toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedContacts = [];
        return;
      }
      try {
        this.isFetchingAllIds = true;
        const response = await CampaignsAPI.getAllContactIds(this.campaignId);
        this.selectedContacts = response.data.contact_ids || [];
      } catch (e) {
        this.showAlert('Failed to select all contacts. Please try again.');
      } finally {
        this.isFetchingAllIds = false;
      }
    },
    selectThisPage() {
      const onPage = this.selectableContactsOnPage;
      if (onPage.length === 0) return;
      if (this.isAllOnPageSelected) {
        this.selectedContacts = this.selectedContacts.filter(id =>
          !onPage.some(c => c.id === id)
        );
      } else {
        const existing = new Set(this.selectedContacts);
        onPage.forEach(c => existing.add(c.id));
        this.selectedContacts = [...existing];
      }
    },
    clearSelection() {
      this.selectedContacts = [];
    },
    async bulkCancelSelectedContacts() {
      if (this.selectedContacts.length === 0) {
        this.showAlert('No contacts selected');
        return;
      }
      try {
        await CampaignsAPI.bulkCancel(this.campaignId, this.selectedContacts);
        this.showAlert('Bulk cancel started in the background. The list will refresh shortly.');
        this.clearSelection();
        setTimeout(() => this.fetchCampaignContacts(), 3000);
      } catch (error) {
        console.error('Bulk cancel failed:', error);
        this.showAlert(error.response?.data?.error || 'Bulk cancel failed. Please try again.');
      }
    },
    async bulkRegenerateSelectedContacts() {
      if (this.selectedContacts.length === 0) {
        this.showAlert('No contacts selected');
        return;
      }
      try {
        await CampaignsAPI.bulkRegenerate(this.campaignId, this.selectedContacts);
        this.showAlert('Bulk regenerate started in the background (executed drafts are skipped). The list will refresh shortly.');
        this.clearSelection();
        setTimeout(() => this.fetchCampaignContacts(), 3000);
      } catch (error) {
        console.error('Bulk regenerate failed:', error);
        this.showAlert(error.response?.data?.error || 'Bulk regenerate failed. Please try again.');
      }
    },
    async bulkApproveSelectedContacts() {
      if (this.selectedContacts.length === 0) {
        this.showAlert('No contacts selected');
        return;
      }

      const selectedContactsData = this.contacts.filter(c => 
        this.selectedContacts.includes(c.id)
      );

      // Count total drafts to approve
      let totalDrafts = 0;
      selectedContactsData.forEach(contact => {
        const approvableDrafts = contact.steps.filter(step => 
          step.draft_id && step.status === 'draft'
        );
        totalDrafts += approvableDrafts.length;
      });

      if (totalDrafts === 0) {
        this.showAlert('No drafts available to approve for selected contacts');
        return;
      }

      // Show confirmation modal
      this.bulkApproveData = {
        contactCount: this.selectedContacts.length,
        totalDrafts: totalDrafts,
      };
      this.showBulkApproveConfirmation = true;
    },
    async confirmBulkApprove() {
      this.isBulkApproving = true;

      try {
        const response = await CampaignsAPI.bulkApprove(
          this.campaignId,
          this.selectedContacts
        );

        this.isBulkApproving = false;
        this.showBulkApproveConfirmation = false;

        // Bulk approve runs in background and returns 202 Accepted
        if (response.status === 202) {
          this.showAlert('Bulk approve started in the background. The list will refresh shortly.');
          this.clearSelection();
          setTimeout(() => this.fetchCampaignContacts(), 3000);
          return;
        }

        const result = response.data;

        // If no drafts were approved, show a simple alert instead of the modal
        if (result.total_approved === 0) {
          let message = 'No drafts were approved.\n\n';
          
          if (result.total_skipped > 0) {
            message += `${result.total_skipped} draft(s) were skipped.\n\n`;
          }
          
          // Show errors
          if (result.contact_results && result.contact_results.length > 0) {
            message += 'Reasons:\n';
            result.contact_results.forEach(contactResult => {
              if (contactResult.errors && contactResult.errors.length > 0) {
                message += `\n${contactResult.contact_name}:\n`;
                contactResult.errors.forEach(error => {
                  message += `  • ${error}\n`;
                });
              }
            });
          }
          
          this.showAlert(message);
        } else {
          // Show detailed results modal if some drafts were approved
          this.showBulkApprovalResults(result);
        }

        this.clearSelection();
        this.fetchCampaignContacts();
      } catch (error) {
        this.isBulkApproving = false;
        this.showBulkApproveConfirmation = false;
        console.error('Bulk approval failed:', error);
        this.showAlert('Bulk approval failed. Please try again.');
      }
    },
    closeBulkApproveConfirmation() {
      this.showBulkApproveConfirmation = false;
    },
    showBulkApprovalResults(results) {
      this.bulkApproveResults = results;
      this.showBulkApproveResults = true;
    },
    closeBulkApproveResults() {
      this.showBulkApproveResults = false;
    },
  },
};
</script>

<style scoped lang="scss">
/* Page Container - height auto so no extra white space below pagination */
.campaign-contacts-page {
  width: calc(100vw - 3.4rem);
  height: auto;
  display: flex;
  flex-direction: column;
  overflow: visible;
  background: #f8fafc;
  padding-bottom: var(--space-normal);

  .dark & {
    background: var(--s-900);
  }
}

.page-back-button {
  padding: var(--space-normal) var(--space-normal) 0 var(--space-normal);
  margin-bottom: var(--space-normal);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-micro);
  color: var(--w-500);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--w-600);
  }
  
  .dark & {
    color: var(--w-400);
    
    &:hover {
      color: var(--w-300);
    }
  }
}

.campaign-contacts-container {
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  padding: 0 var(--space-normal) var(--space-normal) var(--space-normal);
  overflow: visible;
  width: 100%;
  
  h2 {
    color: var(--s-900);
    
    .dark & {
      color: var(--s-50);
    }
  }
  
  p {
    color: var(--s-600);
    
    .dark & {
      color: var(--s-400);
    }
  }
}

.mb-6 {
  margin-bottom: 1.5rem;
}

/* Bulk Actions Bar */
.bulk-actions-bar {
  background: var(--b-50);
  border: 1px solid var(--b-200);
  border-radius: var(--border-radius-normal);
  padding: var(--space-normal);
  margin-bottom: var(--space-normal);
  box-shadow: var(--shadow-small);

  .dark & {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.3);
  }
}

.bulk-actions-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.bulk-actions-text {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--b-800);

  .dark & {
    color: var(--b-300);
  }
}

.bulk-actions-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Campaign contacts table - clean minimal style with rounded corners */
.campaign-contacts-table-wrapper {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  .dark & {
    background: var(--s-800);
    border-color: var(--s-700);
  }
}

/* Loader only inside table area (not full page) */
.table-loading-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 2rem;
}

/* When screen < 1024px, table wrapper scrolls horizontally so content is not hidden */
@media (max-width: 1023px) {
  .campaign-contacts-table-wrapper {
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
  }
}

.campaign-contacts-footer {
  position: fixed;
  bottom: 0;
  width: calc(100vw - 3.5rem);
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: var(--space-small);

  .dark & {
    background: var(--s-800);
    border-color: var(--s-700);
  }
}

.table-scroll-wrapper {
  width: 100%;
  min-width: 0;
  max-height: calc(100vh - 16rem);
  padding: 0;
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-gutter: stable;
  overflow-anchor: none;
  overscroll-behavior-y: contain;
  border-radius: 0 0 8px 8px;
  -webkit-overflow-scrolling: touch;
  transition: max-height 0.2s ease;

  .bulk-bar-open & {
    max-height: calc(100vh - 21rem);
  }
}

.table-scroll-wrapper > .campaign-contacts-table {
  margin-top: 0;
}

.campaign-contacts-table {
  width: 100%;
  margin: 0;
  border-collapse: collapse;
  border-spacing: 0;
  min-width: 900px;
  table-layout: auto;

  thead tr {
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;

    .dark & {
      background: var(--s-900);
      border-color: var(--s-700);
    }
  }

  thead th {
    position: sticky;
    top: -1px;
    z-index: 10;
    background: #f5f5f5;
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #616161;
    border-right: 1px solid #e0e0e0;
    box-shadow: 0 1px 0 0 #e0e0e0;

    &:first-child {
      border-radius: 8px 0 0 0;
    }

    &:last-child {
      border-right: none;
      border-radius: 0 8px 0 0;
    }

    .dark & {
      background: var(--s-900);
      color: var(--s-400);
      border-right-color: var(--s-700);
      box-shadow: 0 1px 0 0 var(--s-700);
    }
  }

  tbody tr {
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
    transition: background-color 0.15s ease;

    &:hover {
      background: #fafafa;
    }

    &.row-selected {
      background: #f5f9ff;

      td:first-child {
        box-shadow: inset 2px 0 0 #1976d2;
      }
    }

    &:last-child td:first-child {
      border-radius: 0 0 0 8px;
    }

    &:last-child td:last-child {
      border-radius: 0 0 8px 0;
    }

    .dark & {
      background: var(--s-800);
      border-color: var(--s-700);

      &:hover {
        background: var(--s-750);
      }

      &.row-selected {
        background: rgba(25, 118, 210, 0.08);

        td:first-child {
          box-shadow: inset 2px 0 0 #42a5f5;
        }
      }
    }
  }

  tbody td {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    vertical-align: middle;
    border-right: 1px solid #e0e0e0;
    color: #424242;

    &:last-child {
      border-right: none;
    }

    .dark & {
      border-right-color: var(--s-700);
      color: var(--s-200);
    }
  }
}

/* Pagination */
.table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-normal);
  padding: var(--space-small) var(--space-normal);
  border-top: 1px solid #e0e0e0;
  background: #fff;

  .dark & {
    border-top-color: var(--s-700);
    background: var(--s-800);
  }
}

.pagination-info {
  font-size: 0.875rem;
  color: #616161;

  .dark & {
    color: var(--s-400);
  }
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: var(--space-small);
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 2px;
}

.pagination-page-btn {
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 0.875rem;
  color: #424242;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #bdbdbd;
  }

  &.active {
    background: #1976d2;
    border-color: #1976d2;
    color: #fff;
  }

  .dark & {
    background: var(--s-800);
    border-color: var(--s-700);
    color: var(--s-200);

    &:hover:not(:disabled) {
      background: var(--s-700);
    }

    &.active {
      background: #1976d2;
      border-color: #1976d2;
      color: #fff;
    }
  }
}

.pagination-per-page {
  display: flex;
  align-items: center;
  gap: var(--space-small);

  label {
    font-size: 0.875rem;
    color: #616161;

    .dark & {
      color: var(--s-400);
    }
  }
}

.per-page-select {
  padding: 0.35rem 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 0.875rem;
  color: #424242;

  .dark & {
    background: var(--s-700);
    border-color: var(--s-600);
    color: var(--s-200);
  }
}

/* Contact column with inline selector (same as contact table) */
.contact-avatar-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;

  .dark & {
    background: rgba(25, 118, 210, 0.25);
    color: #90caf9;
  }
}

.table-cell-contact {
  .name-cell-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 0.2s;
    flex-shrink: 0;

    &:hover {
      background-color: rgba(59, 130, 246, 0.1);
    }

    .dark &:hover {
      background-color: rgba(59, 130, 246, 0.15);
    }

    .checkbox {
      cursor: pointer;
      width: 16px;
      height: 16px;
      margin: 0;
    }
  }

  .contact-name-btn {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    cursor: pointer;
    padding: 0.25rem 0;
    margin: 0;
    border: none;
    background: transparent;
    transition: opacity 0.15s;
    flex: 1;
    min-width: 0;

    &:hover {
      opacity: 0.85;

      .dark & {
        opacity: 0.9;
      }
    }
  }

  .contact-name {
    font-weight: 500;
    color: #212121;

    .dark & {
      color: var(--s-100);
    }
  }

  .contact-email {
    font-size: 0.8125rem;
    color: #757575;

    .dark & {
      color: var(--s-400);
    }
  }
}

.table-cell-status .status-pill {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 2px;
  text-transform: lowercase;

  &.bg-red-100 {
    background: #ffebee !important;
    color: #c62828 !important;
  }

  .dark &.bg-red-100 {
    background: rgba(198, 40, 40, 0.2) !important;
    color: #ef9a9a !important;
  }

  /* Executed: green for completed/success */
  &.badge-executed {
    background: rgba(22, 163, 74, 0.15) !important;
    color: #15803d !important;

    .dark & {
      background: rgba(34, 197, 94, 0.2) !important;
      color: #4ade80 !important;
    }
  }
}

/* Executed badge (e.g. in modals): same blue as primary nav */
.badge-executed {
  background: rgba(22, 163, 74, 0.15) !important;
  color: #15803d !important;

  .dark & {
    background: rgba(34, 197, 94, 0.2) !important;
    color: #4ade80 !important;
  }
}

.view-emails-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.35rem 0.65rem;
  font-size: 0.8125rem;
  color: #616161;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;

  &:hover {
    background: #f5f5f5;
    border-color: #bdbdbd;
  }

  .dark & {
    background: var(--s-700);
    border-color: var(--s-600);
    color: var(--s-200);

    &:hover {
      background: var(--s-650);
    }
  }

  .view-emails-chevron {
    margin-left: 0.25rem;
    opacity: 0.8;
  }
}

.table-cell-draft-status {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  .draft-status-text {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #424242;

    .dark & {
      color: var(--s-300);
    }
  }

  .draft-status-dots {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;

    &.bg-red-500 {
      background: #c62828 !important;
    }

    /* Executed: same blue as primary nav (#40518A) */
    &.status-dot-executed {
      background: #40518A !important;

      .dark & {
        background: #6e86d8 !important;
      }
    }

    /* Cancelled: light orange (failed stays red) */
    &.status-dot-cancelled {
      background: #d68137 !important;

      .dark & {
        background: #d68137 !important;
      }
    }
  }
}

/* Status dots in email cards: blue executed, light orange cancelled, red failed */
.status-dot-executed {
  background: #40518A !important;

  .dark & {
    background: #6e86d8 !important;
  }
}

.status-dot-cancelled {
  background: #d68137 !important;

  .dark & {
    background: #d68137 !important;
  }
}

.table-cell-num {
  font-weight: 500;
  color: #424242;

  .dark & {
    color: var(--s-200);
  }
}

.table-cell-actions .actions-icons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.table-cell-actions .action-btn {
  min-width: 2rem;
  padding: 0.25rem;
  border-radius: 2px;
}

.table-cell-actions .action-btn-approve :deep(.icon) {
  color: #2e7d32;
}

.table-cell-actions .action-btn-cancel :deep(.icon) {
  color: #c62828;
}

.step-cell {
  cursor: pointer;
}

// Email List Styles (expanded row - matches table aesthetic)
.email-list-row {
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;

  .dark & {
    background-color: var(--s-850);
    border-bottom-color: var(--s-700);
  }
}

.email-list-cell {
  padding: var(--space-normal) !important;
  background: #fafafa;
  border-right: 1px solid #e0e0e0;

  &:last-child {
    border-right: none;
  }

  .dark & {
    background: var(--s-900);
    border-right-color: var(--s-700);
  }
}

.email-list-container {
  padding: 0.5rem 0;
  width: 100%;
}

.email-card {
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.gap-3 {
  gap: 0.75rem;
}

.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.contact-info-modal {
  :deep(.modal-container) {
    max-width: 500px;
    width: 90%;
    max-height: 85vh;
    overflow: hidden;
  }
  /* Reset paragraph margin so contact attributes (email, phone, etc.) don't get 0.5rem top margin */
  :deep(.modal-container p) {
    margin-top: 0;
  }
}

.modal-content-wrapper {
  height: 100%;
  max-height: 80vh;
  overflow-y: auto;
  
  :deep(.contact--panel) {
    position: relative;
    height: 100%;
    width: 100%;
    box-shadow: none;
    transform: none;
    top: 0;
    left: 0;
  }
  
  :deep(.special-panel-styles) {
    position: relative;
    height: 100%;
    width: 100%;
    box-shadow: none;
    transform: none;
    top: 0;
    left: 0;
  }
}

// Draft Modal Styles
.draft-modal {
  :deep(.modal-container) {
    max-width: 700px;
    width: 95%;
    max-height: 90vh;
    border-radius: 0.75rem;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.draft-modal-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.draft-viewer {
  padding: 1.5rem;
}

.draft-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-border);

  .draft-modal-title {
    margin: 0;
    flex: 1;
    min-width: 0;
  }

  .draft-header-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
  }
}

.draft-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-item-scheduled-picker {
  min-width: 0;
}

.draft-modal-datetime-picker {
  width: 100%;

  :deep(.mx-datepicker) {
    width: 100%;
  }

  :deep(.mx-input-wrapper) {
    width: 100%;
  }
}

.info-item {
  background: var(--s-50);
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--color-border);

  .dark & {
    background: var(--s-800);
    border-color: var(--s-700);
  }
  
  .info-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--s-500);
    text-transform: uppercase;
    margin-bottom: 0.25rem;

    .dark & {
      color: var(--s-400);
    }
  }
  
  .info-value {
    font-size: 0.875rem;
    color: var(--s-800);

    .dark & {
      color: var(--s-100);
    }
  }
}

.label-badge-inline {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 500;
}

/* Trigger label: no extra left space */
.info-item-trigger-label .info-value.label-badge-inline {
  padding-left: 0;
  margin-left: 0;
  display: block;
}

.draft-section {
  margin-bottom: 1.5rem;
  
  .section-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--s-700);
    margin-bottom: 0.5rem;

    .dark & {
      color: var(--s-200);
    }
  }
  
  .section-content {
    background: var(--s-50);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin: 0;
    border: 1px solid var(--color-border);
    color: var(--s-800);

    .dark & {
      background: var(--s-800);
      color: var(--s-100);
      border-color: var(--s-700);
    }
  }
}

.subject-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: var(--w-500);
    box-shadow: 0 0 0 2px rgba(var(--w-500-rgb), 0.1);
  }
}

.editor-wrapper {
  border: 1px solid var(--color-border);
  border-radius: 0.375rem;
  overflow: hidden;
  
  .message-editor {
    min-height: 200px;
  }
}

.message-textarea {
  width: 100%;
  padding: 0.75rem;
  border: none;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 200px;
  
  &:focus {
    outline: none;
  }
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  .attachment-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.attachment-upload {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .hidden {
    display: none;
  }
}

.content-display {
  background: var(--s-50);
  padding: 1rem;
  border-radius: 0.5rem;
  min-height: 150px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: var(--s-800);
  border: 1px solid var(--color-border);

  .dark & {
    background: var(--s-800);
    color: var(--s-100);
    border-color: var(--s-700);
  }
}

.datetime-input {
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.draft-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border);
}

.no-draft-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.regenerate-modal {
  :deep(.modal-container) {
    max-width: 500px;
    width: 90%;
  }
}

.regenerate-content {
  textarea {
    border-color: var(--color-border);
    
    &:focus {
      outline: none;
      border-color: var(--w-500);
    }
  }
}

/* Modal Content Styles */
.modal-content {
  padding: var(--space-normal) var(--space-large);
  
  p {
    margin: 0 0 var(--space-normal) 0;
    font-size: var(--font-size-small);
    color: var(--s-700);
    line-height: 1.5;
    
    .dark & {
      color: var(--s-300);
    }
    
    strong {
      color: var(--s-900);
      font-weight: var(--font-weight-medium);
      
      .dark & {
        color: var(--s-100);
      }
    }
  }
}

.info-box {
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-normal);
  margin-bottom: var(--space-normal);
  
  .dark & {
    background: var(--s-700);
    border-color: var(--s-600);
  }
  
  p {
    margin: 0;
    font-size: var(--font-size-small);
    color: var(--s-800);
    
    .dark & {
      color: var(--s-200);
    }
  }
}

.note-box {
  background: var(--y-50);
  border-left: 3px solid var(--y-500);
  border-radius: var(--border-radius-normal);
  padding: var(--space-normal);
  margin-bottom: 0;
  
  .dark & {
    background: rgba(251, 191, 36, 0.1);
    border-left-color: var(--y-600);
  }
  
  p {
    margin: 0;
    font-size: var(--font-size-mini);
    color: var(--s-700);
    line-height: 1.6;
    
    .dark & {
      color: var(--s-300);
    }
  }
}

.modal-footer {
  padding: var(--space-normal) var(--space-large);
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  background: transparent;
}

/* Results Modal Specific Styles */
.results-content {
  max-height: 60vh;
  overflow-y: auto;
}

.results-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-small);
  margin-bottom: var(--space-normal);
}

.summary-item {
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small) var(--space-normal);
  text-align: center;
  
  .dark & {
    background: var(--s-700);
    border-color: var(--s-600);
  }
  
  &.success {
    background: var(--g-50);
    border-color: var(--g-300);
    
    .dark & {
      background: rgba(34, 197, 94, 0.15);
      border-color: rgba(34, 197, 94, 0.3);
    }
    
    .summary-label {
      color: var(--g-800);
      
      .dark & {
        color: var(--g-400);
      }
    }
    
    .summary-value {
      color: var(--g-900);
      
      .dark & {
        color: var(--g-300);
      }
    }
  }
  
  &.error {
    background: var(--r-50);
    border-color: var(--r-300);
    
    .dark & {
      background: rgba(239, 68, 68, 0.15);
      border-color: rgba(239, 68, 68, 0.3);
    }
    
    .summary-label {
      color: var(--r-800);
      
      .dark & {
        color: var(--r-400);
      }
    }
    
    .summary-value {
      color: var(--r-900);
      
      .dark & {
        color: var(--r-300);
      }
    }
  }
}

.summary-label {
  display: block;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: var(--s-700);
  margin-bottom: var(--space-micro);
  
  .dark & {
    color: var(--s-300);
  }
}

.summary-value {
  display: block;
  font-size: var(--font-size-large);
  font-weight: var(--font-weight-bold);
  color: var(--s-900);
  
  .dark & {
    color: var(--s-100);
  }
}

.results-details {
  background: var(--color-background-light);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-normal);
  margin-bottom: var(--space-normal);
  
  .dark & {
    background: var(--s-700);
    border-color: var(--s-600);
  }
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-small) 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
    
    .dark & {
      border-color: var(--s-600);
    }
  }
}

.detail-label {
  font-size: var(--font-size-small);
  color: var(--s-700);
  font-weight: var(--font-weight-normal);
  
  .dark & {
    color: var(--s-300);
  }
}

.detail-value {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--s-900);
  
  .dark & {
    color: var(--s-100);
  }
}

.results-errors {
  margin-top: var(--space-normal);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border);
}

.errors-title {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--r-700);
  margin: 0 0 var(--space-small) 0;
  
  .dark & {
    color: var(--r-400);
  }
}

.error-item {
  background: var(--r-50);
  border-left: 3px solid var(--r-500);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small) var(--space-normal);
  margin-bottom: var(--space-small);
  
  .dark & {
    background: rgba(239, 68, 68, 0.15);
    border-left-color: var(--r-600);
  }
}

.error-contact {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--r-900);
  margin: 0 0 var(--space-micro) 0;
  
  .dark & {
    color: var(--r-300);
  }
}

.error-list {
  margin: 0;
  padding-left: var(--space-normal);
  list-style-type: disc;
  
  li {
    font-size: var(--font-size-mini);
    color: var(--r-800);
    margin-bottom: var(--space-micro);
    line-height: 1.4;
    
    .dark & {
      color: var(--r-400);
    }
  }
}
</style>
